import { db } from "@/lib/firebase";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from "firebase/firestore";

export type FeaturedBusinessRow = {
  id: string;
  name?: string;
  slug?: string;
  logoUrl?: string;
  category?: string;
  subCategory?: string | string[];
  cityLine?: string;
  addressLine?: string;
  isOpenNow?: boolean;
  featuredRank?: number;
};

type ListenFeaturedBusinessesOptions = {
  limitCount?: number;
  excludeBusinessId?: string | null;
};

type BusinessSearchFeaturedDoc = {
  businessId?: string | null;
  slug?: string | null;

  businessName?: string | null;
  businessNameLower?: string | null;

  categoryName?: string | null;
  subcategoryName?: string | null;

  countyName?: string | null;
  cityName?: string | null;
  areaName?: string | null;

  shortDescription?: string | null;

  logoUrl?: string | null;

  isFeatured?: boolean;
  featuredRank?: number | null;

  status?: string | null;
  isPublished?: boolean;
  isDeleted?: boolean;
  isDeactivated?: boolean;

  /**
   * Optional compatibility fields.
   * These help if some old index documents still contain old names.
   */
  name?: string | null;
  category?: string | null;
  subCategory?: string | string[] | null;
  cityLine?: string | null;
  addressLine?: string | null;
  isOpenNow?: boolean | null;
};

function cleanString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;

  const clean = value.trim();
  return clean.length > 0 ? clean : undefined;
}

function cleanNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function buildCityLine(data: BusinessSearchFeaturedDoc): string | undefined {
  const directCityLine = cleanString(data.cityLine);

  if (directCityLine) {
    return directCityLine;
  }

  return [
    cleanString(data.areaName),
    cleanString(data.cityName),
    cleanString(data.countyName),
  ]
    .filter(Boolean)
    .join(", ");
}

function toFeaturedBusinessRow(
  id: string,
  data: BusinessSearchFeaturedDoc
): FeaturedBusinessRow {
  return {
    id: cleanString(data.businessId) ?? id,

    name:
      cleanString(data.businessName) ??
      cleanString(data.name) ??
      "Business",

    slug: cleanString(data.slug),

    logoUrl: cleanString(data.logoUrl),

    category:
      cleanString(data.categoryName) ??
      cleanString(data.category),

    subCategory:
      cleanString(data.subcategoryName) ??
      data.subCategory ??
      undefined,

    cityLine: buildCityLine(data),

    addressLine:
      cleanString(data.addressLine) ??
      cleanString(data.shortDescription),

    isOpenNow: data.isOpenNow === true,

    featuredRank: cleanNumber(data.featuredRank),
  };
}

export function listenFeaturedBusinesses(
  options: ListenFeaturedBusinessesOptions,
  onData: (rows: FeaturedBusinessRow[]) => void,
  onError?: (error: unknown) => void
): Unsubscribe {
  const { limitCount = 10, excludeBusinessId = null } = options;

  /**
   * IMPORTANT:
   * Read from businessSearch, not businesses.
   *
   * Your public /businesses rule also checks subscriptionStatus.
   * That makes a public featured carousel query harder to satisfy.
   *
   * businessSearch is the safer public listing index because its rule only
   * requires:
   * - approved
   * - published
   * - not deleted
   * - not deactivated
   */
  const q = query(
    collection(db, "businessSearch"),
    where("status", "==", "approved"),
    where("isPublished", "==", true),
    where("isDeleted", "==", false),
    where("isDeactivated", "==", false),
    where("isFeatured", "==", true),
    orderBy("featuredRank", "asc"),
    orderBy("businessNameLower", "asc"),
    limit(limitCount + (excludeBusinessId ? 1 : 0))
  );

  return onSnapshot(
    q,
    (snap) => {
      let rows = snap.docs.map((docSnap) =>
        toFeaturedBusinessRow(
          docSnap.id,
          docSnap.data() as BusinessSearchFeaturedDoc
        )
      );

      if (excludeBusinessId) {
        rows = rows.filter((row) => row.id !== excludeBusinessId);
      }

      onData(rows.slice(0, limitCount));
    },
    (error) => {
      onError?.(error);
    }
  );
}